<template>
    <div class="w-full space-y-2">
        <ClientOnly>
            <CButton
                v-if="isSkillOwner"
                full
                icon="edit"
                size="large"
                :to="{
                    name: 'profile-skills-edit-slug',
                    params: { slug: skill.slug || skill.id }
                }"
                variant="primary-150"
            >
                Upraviť
            </CButton>

            <template v-else-if="isAbleToJoin && !hasCurriculum">
                <AskForMembershipButton v-if="isWorkingGroup" :org="org" :skill="skill" />

                <CButton
                    v-else-if="hasStudyLink"
                    full
                    :href="skill.studyLink"
                    size="large"
                    target="_blank"
                    variant="success"
                    @click="trackButtonClick()"
                >
                    Viac info a prihláška
                </CButton>
            </template>

            <template #fallback>
                <CButton v-if="hasStudyLink && !hasCurriculum" full :href="skill.studyLink" size="large" target="_blank" variant="success" aria-label="Viac info a prihláška">Viac info a prihláška</CButton>
            </template>
        </ClientOnly>
        <template v-if="hasCurriculum">
            <CButton full href="#course-content" size="large" variant="primary" aria-label="Prejsť na lekcie kurzu">Prejsť na lekcie</CButton>
            <a v-if="hasStudyLink" :href="skill.studyLink" target="_blank" rel="noopener noreferrer" class="block text-center text-sm text-txt-brand-action underline" @click="trackButtonClick()">Web kurzu a prihláška</a>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useExponea } from '@/composables/exponea/useExponea'
import { useCheese } from '@/composables/useCheese'
import { SkillType } from '#shared/types/Skill'
import type { Skill, SkillOrgPreview } from '#shared/types/Skill'
import { hasLessonContent } from '#shared/utils/course'

interface Props {
    skill: Skill
    org: SkillOrgPreview
}

const props = defineProps<Props>()
const store = useApplicationStore()
const { trackExponeaEvent } = useExponea()
const { track: trackCheese } = useCheese()
const hasCurriculum = computed(() => props.skill.type === SkillType.kurz && props.skill.lessons?.some((lesson) => lesson.title?.trim() && hasLessonContent(lesson)))

const isAbleToJoin = computed((): boolean => {
    return isWorkingGroup.value || hasStudyLink.value
})

const isSkillOwner = computed((): boolean => {
    const userId = store.userId
    return userId ? props.org.memberIds?.includes(userId) : false
})

const isWorkingGroup = computed((): boolean => {
    return props.skill.type === SkillType.workgroup
})

const hasStudyLink = computed((): boolean => {
    return (
        (props.skill.type === SkillType.kurz ||
            props.skill.type === SkillType.event ||
            props.skill.type === SkillType.studyOpportunity) &&
        !!props.skill.studyLink?.trim()
    )
})

function trackButtonClick() {
    trackCheese('education_interest_clicked', { skill_id: props.skill.id || '', type: props.skill.type || '' })

    let eventName = 'cta1_zaujem_o_skill_undefined_type'
    switch (props.skill.type) {
        case SkillType.event:
            eventName = 'cta1_zaujem_o_udalost'
            break
        case SkillType.kurz:
            eventName = 'cta1_zaujem_o_kurz'
            break
        case SkillType.studyOpportunity:
            eventName = 'cta1_zaujem_o_studium'
    }

    trackExponeaEvent({
        eventName,
        userId: store.userId,
        eventProperties: {
            type: props.skill.type,
            title: props.skill.title,
            org_title: props.org.title,
            link: props.skill.studyLink
        }
    })
}
</script>
